<script>
import Vue from 'vue'
import metaInfo from '@/modules/mixins/metaInfo'
import store from '@/modules/store'
import Spinner from '@/components/atoms/Spinner'
import ImageWrapper from '@/components/atoms/ImageWrapper'
import ButtonLoader from '@/components/atoms/ButtonLoader'
import Box from '@/components/atoms/Box'
import DeliveryMap from '@/components/molecules/DeliveryMap'
import SummaryOrderDetails from '@/components/molecules/SummaryOrderDetails'
import OrderStatus from '@/components/molecules/OrderStatus'
import NotificationModal from '@/components/organism/restaurants/NotificationModal'

import event from '@/modules/mixins/event'
import pusher from '@/modules/mixins/pusher'

import { PublicOrder, Order, TrackOrder } from '@/api'
import NotificationAlert from '@/components/organism/restaurants/NotificationAlert.vue'
import sharedTracking from '@/modules/mixins/sharedTracking'
import Config from '@/config.loader'
import { reportPurchase } from '@/modules/analytics/purchase'

const cart = Vue.prototype.$cart

const publicOrder = Vue.observable(new PublicOrder())
const privateOrder = Vue.observable(new Order())
const trackOrder = Vue.observable(new TrackOrder())
const restaurant = Vue.prototype.$restaurant
const PURCHASE_RETRY_MS = 10000
const PURCHASE_RETRY_LIMIT = 12

export default {
    name: 'Page',
    components: {
        NotificationAlert,
        OrderStatus,
        SummaryOrderDetails,
        DeliveryMap,
        Box,
        ButtonLoader,
        ImageWrapper,
        Spinner,
        NotificationModal,
    },
    mixins: [event, pusher, metaInfo, sharedTracking],
    data() {
        return {
            error: false,
            errorDetail: null,
            loaded: false,
            reorderLoading: false,
            orderError: null,
            countDown: null,
        }
    },
    computed: {
        order() {
            return this.isSharedTrackingPage ? publicOrder : privateOrder
        },
        trackOrder: () => trackOrder,
        pageTitle() {
            return this.$t('labels.summary')
        },
        deliveryJob() {
            return this.$store.getters['summary/deliveryJob']
        },
        deliveryStatus() {
            return this.$store.getters['summary/deliveryStatus']
        },
        model() {
            return this.$cart?.model
        },
        mapOrder() {
            // the public link refreshes the store from Mercure, the module-level publicOrder model is not refreshed;
            // state, not the getter: this is first evaluated (by the isDeliveryProcessed watcher) before created()
            // registers the summary module, and a missing getter key would never become reactive
            return this.isSharedTrackingPage ? this.$store.state.summary?.order : this.order
        },
        isDeliveryProcessed() {
            return !!(this.mapOrder?.is_delivery_processed && this.deliveryJob?.id)
        },
        latitude() {
            return this.isDeliveryProcessed ? this.deliveryJob?.courier_lat : undefined
        },
        longitude() {
            return this.isDeliveryProcessed ? this.deliveryJob?.courier_long : undefined
        },
        showDelivery() {
            return Boolean(this.latitude && this.longitude)
        },
        pickup() {
            if (
                this.mapOrder?.restaurant?.pickup_information?.latitude &&
                this.mapOrder?.restaurant?.pickup_information?.longitude
            )
                return {
                    lat: this.mapOrder?.restaurant?.pickup_information.latitude,
                    lng: this.mapOrder?.restaurant?.pickup_information.longitude,
                }
            else
                return (
                    this.mapOrder?.restaurant?.pickup_information?.full_address ||
                    this.mapOrder?.restaurant?.location
                )
        },
        dropoff() {
            if (
                this.mapOrder?.delivery_address_details?.lat &&
                this.mapOrder?.delivery_address_details?.lon
            )
                return {
                    lat: this.mapOrder.delivery_address_details.lat,
                    lng: this.mapOrder.delivery_address_details.lon,
                }
            else return this.mapOrder?.delivery_full_address
        },
    },
    watch: {
        isDeliveryProcessed: {
            handler(isDeliveryProcessed) {
                this.startTracking(isDeliveryProcessed)
            },
        },
    },

    beforeDestroy() {
        this.pageDestroyed = true
        this.closePublicStream()
        clearTimeout(this.publicStreamRetry)
        clearTimeout(this.canteenTimer)
        this.$store.unregisterDelivery('summary')
        this.purchaseStopped = true
        clearTimeout(this.purchaseTimer)
    },

    async created() {
        this.pageDestroyed = false
        this.mercureSource = null
        this.publicGeneration = 0
        this.canteenTimer = null
        this.publicStreamRetry = null
        this.publicStreamDelay = 5000
        this.pusherWasConnected = false
        this.purchaseTimer = null
        this.purchaseChecks = 0
        this.purchaseStopped = false
        this.purchasePending = false
        this.purchaseRequest = false
        this.purchaseRecheck = false
        store.registerDelivery('summary')
        await store.dispatch('summary/resetDeliveryStatus')

        const routeId = this.$route.params.id
        const orderRequest = this.order.getOrder(routeId)
        if (!this.isSharedTrackingPage)
            orderRequest.then((order) => this.handlePurchase(order, routeId)).catch(() => {})
        const apis = this.isSharedTrackingPage
            ? [orderRequest]
            : [orderRequest, trackOrder.fetch(routeId)]

        Promise.all(apis)
            .then(async (results) => {
                if (!this.isCurrent(routeId)) return
                const order = results[0]
                const trackOrder = this.isSharedTrackingPage ? results[0] : results[1]

                const restaurantData = this.isSharedTrackingPage
                    ? order.restaurant
                    : await restaurant.fetchRestaurant(order.restaurant.slug)
                // every network await is followed by a check before the next dispatch
                if (!this.isCurrent(routeId)) return
                await store.dispatch('summary/setRestaurant', restaurantData)
                await store.dispatch('summary/setOrder', order)
                await store.dispatch('summary/setTracking', trackOrder)

                if (!store.getters['summary/orderCancelled']) {
                    if (this.isSharedTrackingPage) {
                        // the public link: Mercure only (Pusher private channels are not authorised here)
                        this.openPublicStream(routeId)
                    } else {
                        this.canteenCutoffTimer()
                        await this.subscribeOrderChannel(this.$route.params.id)
                        if (this.channelOrder)
                            this.channelOrder.bind('update-order', () =>
                                this.onPurchaseOrderPush(routeId)
                            )
                        this.bindPusherReconnect(routeId)
                        if (!order.is_delivery_processed) {
                            await this.order.getOrder(routeId)
                            if (!this.isCurrent(routeId)) return
                        }
                        await this.startTracking(order.is_delivery_processed)
                    }
                }
                this.loaded = true
                if (order.cancel_reason || this.$route.params.error)
                    store.dispatch(
                        'summary/setOrderError',
                        order.cancel_reason || this.$route.params.error.message
                    )
            })
            .catch(async (e) => {
                if (!this.isCurrent(routeId)) return
                const isSharedTrackingPage = this.isSharedTrackingPage

                if (isSharedTrackingPage && (e.detail || e.message) === 'Not Found') {
                    if (this.$user?.isLoggedIn) {
                        await this.$router.push({ name: 'profile-orders' })
                    } else {
                        await this.$router.push({ name: 'restaurant-list' })
                    }
                    store.dispatch('toastWarning', {
                        title: this.$t(`alerts.order_share.title`),
                        text: this.$t(`alerts.order_share.desc`),
                    })
                }

                if (!isSharedTrackingPage) cart.resetCart()
                this.loaded = true
                this.error = true
                this.errorDetail = e.detail
            })
    },

    methods: {
        isCurrent(routeId) {
            return !this.pageDestroyed && String(this.$route.params.id) === String(routeId)
        },
        openPublicStream(routeId) {
            const host = Config.getConfigValue('VUE_APP_MERCURE_HOST')
            if (!host || this.mercureSource) return
            const source = new EventSource(
                `${host}/.well-known/mercure?topic=/public/order/tracking/${routeId}`
            )
            source.onmessage = (event) => this.mercureUpdate(event, routeId)
            // the first open and every reconnect: re-sync the snapshot that may have been missed meanwhile
            source.onopen = () => {
                this.publicStreamDelay = 5000
                this.$store.dispatch('summary/tick')
                this.refreshPublicSnapshot(routeId)
            }
            source.onerror = () => {
                // the browser retries network errors itself; an HTTP-level failure (e.g. a proxy 502 while the hub
                // restarts) closes the stream for good, so reopen it with a capped backoff
                if (this.mercureSource !== source || source.readyState !== EventSource.CLOSED)
                    return
                this.mercureSource = null
                clearTimeout(this.publicStreamRetry)
                this.publicStreamRetry = setTimeout(() => {
                    if (this.isCurrent(routeId)) this.openPublicStream(routeId)
                }, this.publicStreamDelay)
                this.publicStreamDelay = Math.min(this.publicStreamDelay * 2, 60000)
            }
            this.mercureSource = source
        },
        closePublicStream() {
            if (this.mercureSource) this.mercureSource.close()
            this.mercureSource = null
        },
        mercureUpdate(event, routeId) {
            if (!this.isCurrent(routeId)) return
            let data
            try {
                data = JSON.parse(event.data)
            } catch (e) {
                return
            }
            if (!data?.order) return
            this.publicGeneration += 1
            this.applyPublicSnapshot(data.order)
        },
        refreshPublicSnapshot(routeId) {
            const generation = ++this.publicGeneration
            // a request-local model: Model#fetch writes into its model before this continuation runs
            new PublicOrder()
                .getOrder(routeId)
                .then((order) => {
                    if (!this.isCurrent(routeId) || generation !== this.publicGeneration) return
                    return this.applyPublicSnapshot(order)
                })
                .catch(() => {})
        },
        bindPusherReconnect(routeId) {
            // pusher-js reconnects by itself but does not replay the events missed while the connection was down,
            // so a reconnect re-fetches the order once (tasks#151)
            this.pusherWasConnected = this.pusher.connection.state === 'connected'
            this.pusher.connection.bind('state_change', ({ current }) => {
                if (current !== 'connected') return
                this.$store.dispatch('summary/tick')
                if (this.pusherWasConnected) this.refreshPrivateSnapshot(routeId)
                this.pusherWasConnected = true
            })
        },
        async refreshPrivateSnapshot(routeId) {
            try {
                // the module-level models are refreshed on purpose: later Pusher diffs merge into them
                const [order, trackOrder] = await Promise.all([
                    this.order.getOrder(routeId),
                    this.trackOrder.fetch(routeId),
                ])
                if (!this.isCurrent(routeId)) return
                await this.$store.dispatch('summary/setOrder', order)
                await this.$store.dispatch('summary/setTracking', trackOrder)
                await this.updateStatus()
            } catch (e) {
                // keep the current state; the next event or reconnect brings the update
            }
        },
        // called by the event mixin when a binlog window value cannot be placed (no application timezone yet)
        refreshWindowSnapshot() {
            const routeId = this.$route.params.id
            if (this.isSharedTrackingPage) this.refreshPublicSnapshot(routeId)
            else this.refreshPrivateSnapshot(routeId)
        },
        async applyPublicSnapshot(order) {
            await store.dispatch('summary/setOrder', order)
            await store.dispatch('summary/setTracking', order)
            await this.updateStatus()
        },
        async startTracking(isDeliveryProcessed) {
            // also triggered by the isDeliveryProcessed watcher; the public link has no private channels
            if (this.isSharedTrackingPage) return
            if (isDeliveryProcessed && !this.trackOrder.isCanteenSubOrder && !this.channelTracker) {
                const routeId = this.$route.params.id
                this.subscribeTrackerChannel(this.deliveryJob?.id)
                const order = await this.order.getOrder(routeId)
                if (!this.isCurrent(routeId)) return
                this.$store.dispatch('summary/setOrder', order)
            }
        },
        // resendConfirmation() {
        //     this.order.resendConfirmation(this.order.id)
        // },
        reorder() {
            this.reorderLoading = true
            this.$cart.buildCart(this.order.model).finally(() => {
                this.reorderLoading = false
            })
        },
        recursion(cutoffTime = 60000) {
            const routeId = this.$route.params.id
            clearTimeout(this.canteenTimer)
            this.canteenTimer = setTimeout(async () => {
                const trackOrder = await this.trackOrder.fetch(this.order.id)
                if (!this.isCurrent(routeId)) return
                this.canteenCutoffTimer()
                this.$store.dispatch('summary/setTracking', trackOrder)
            }, cutoffTime)
        },
        canteenCutoffTimer() {
            if (this.trackOrder.isCanteenSubOrder) {
                const cutoffTime = this.$moment(this.trackOrder.canteenCutoffTime).diff(
                    this.$moment()
                )

                if (cutoffTime > 0) this.recursion(cutoffTime)
                else if (this.trackOrder?.canteenMasterOrder?.deliveryJob?.id)
                    this.subscribeTrackerChannel(
                        this.trackOrder?.canteenMasterOrder?.deliveryJob?.id
                    )
                else this.recursion()
            }
        },
        isPurchaseRelevant(routeId) {
            return (
                !this.purchaseStopped &&
                !this.isSharedTrackingPage &&
                String(this.$route.params.id) === String(routeId)
            )
        },
        async handlePurchase(order, routeId) {
            const result = await reportPurchase(order, () => this.isPurchaseRelevant(routeId))
            this.purchasePending = result === 'pending'
            if (this.purchasePending) this.schedulePurchaseCheck(routeId)
        },
        schedulePurchaseCheck(routeId) {
            if (
                this.purchaseStopped ||
                this.purchaseTimer ||
                this.purchaseChecks >= PURCHASE_RETRY_LIMIT
            )
                return
            this.purchaseTimer = setTimeout(() => {
                this.purchaseTimer = null
                this.purchaseChecks += 1
                this.checkPurchase(routeId)
            }, PURCHASE_RETRY_MS)
        },
        checkPurchase(routeId) {
            if (this.purchaseStopped || this.purchaseRequest) return
            this.purchaseRequest = true
            new Order()
                .getOrder(routeId)
                .then((order) => this.handlePurchase(order, routeId))
                .catch(() => this.schedulePurchaseCheck(routeId))
                .finally(() => {
                    this.purchaseRequest = false
                    // A push that arrived during this request may confirm what the request still saw as pending.
                    const recheck = this.purchaseRecheck
                    this.purchaseRecheck = false
                    if (recheck && this.purchasePending && !this.purchaseStopped)
                        this.onPurchaseOrderPush(routeId)
                })
        },
        // Also after the polling budget is used up: a payment-confirmation push re-checks while still pending.
        onPurchaseOrderPush(routeId) {
            if (!this.purchasePending) return
            if (this.purchaseRequest) {
                this.purchaseRecheck = true
                return
            }
            clearTimeout(this.purchaseTimer)
            this.purchaseTimer = null
            this.checkPurchase(routeId)
        },
    },
}
</script>

<template lang="pug">
.summary-page.container-fluid.container-lg.pt-page
    template(v-if="loaded && !error")
        transition(name="fade")
            delivery-map.overflow-hidden(
                v-if="deliveryJob && pickup && dropoff"
                :visible="showDelivery"
                :pickup="pickup"
                :courier="{lat: this.latitude, lng: this.longitude}"
                :dropoff="dropoff"
                :updated-at="deliveryJob.updated_at"
                fill)

        .row.align-items-start.pt-md-6
            .col-12.col-md-8
                order-status
            .col-12.col-md-4
                summary-order-details.mt-6.mt-md-0



    .full-center(v-else-if="loaded && error")
        .d-flex.flex-column.align-items-center.text-white
            icon.mb-4.text-primary(icon="fa-solid fa-exclamation-circle" size="4x")
            h1 {{$t('base.error_occurred')}}


    template(v-else)
        spinner.full-center(loading)
</template>
